// displayAddress — THE ONE function that decides what address a card shows, and which member faces it.
//
// Owner ruling Q1 (2026-09-05). Source order:
//   1. a confidently resolved CANONICAL property address (the address resolver's own verdict:
//      a street link with basis adjudicated / manual / canonical_name, carried by the server as
//      address_state.kind === 'canonical');
//   2. otherwise the address of the eligible ACTIVE member that caused the current search match
//      (the member whose own, not inherited, text contains the street link's recorded witness);
//   3. otherwise the best active property source (the origin if it is active, else the oldest
//      active member, else — history only — the oldest member of all).
// Never the address of whichever row happened to become the representative.
//
// Owner ruling Q2: when current source claims name TWO different streets and the resolver has not
// settled it (address_state.kind === 'conflict'), the card SURFACES the conflict rather than
// choosing a side: it shows the matched member's address when a street search found it (that is
// the claim the agent is looking at) or the best active source otherwise, and carries
// `conflict: [claims…]` so the face can mark it. No claim is dropped or rewritten here.
//
// Four places used to decide this (deskFeed's promotion, unifiedGroups.pickRep, summarize(head),
// HistoryGroup's anchor) and the owner's card needed all four aligned before it changed (B1210).
// They now all call this. Invariant: DISPLAY_ADDRESS_IS_NOT_REPRESENTATIVE_ADDRESS
// (displayAddress.test.mjs proves a representative row with a different address never titles).

const isActive = (l) => l && l.status === 'active'
const namesWitness = (l) => {
  const w = l?.matched_street?.matched_name
  return !!(w && !l.address_inherited && typeof l.address === 'string' && l.address.includes(w))
}
const byOldest = (a, b) => (new Date(a.listed_at || 0) - new Date(b.listed_at || 0)) || String(a.listing_id || '').localeCompare(String(b.listing_id || ''))

/**
 * @param {object[]} members   the cluster's listing rows as the server shapes them
 *                             (address, address_inherited, status, listed_at, is_reshare, matched_street, matched_filter)
 * @param {object}   [addressState] server address_state for the property: {kind:'canonical'|'conflict'|'claim'|'none', text?, claims?}
 * @returns {{ address: string|null, source: 'canonical'|'match'|'active'|'history'|'none', member: object|null, conflict: object[]|null }}
 */
export function displayAddressFor (members, addressState = null) {
  const ms = Array.isArray(members) ? members.filter(Boolean) : []
  const conflict = addressState?.kind === 'conflict' ? (addressState.claims || []) : null
  if (addressState?.kind === 'canonical' && addressState.text) {
    return { address: addressState.text, source: 'canonical', member: null, conflict }
  }
  const active = ms.filter(isActive)
  const match = active.filter(namesWitness).sort(byOldest)[0]
  if (match && match.address) return { address: match.address, source: 'match', member: match, conflict }
  const origin = active.find(l => !l.is_reshare && l.address && !l.address_inherited)
  const best = origin || active.filter(l => l.address).sort(byOldest)[0]
  if (best) return { address: best.address, source: 'active', member: best, conflict }
  const hist = ms.filter(l => l.address).sort(byOldest)[0]
  if (hist) return { address: hist.address, source: 'history', member: hist, conflict }
  return { address: null, source: 'none', member: null, conflict }
}

/**
 * The cluster's FACE: the member the card is built on, already stamped with the display address.
 * The face member is the display member when there is one (so the address and the row agree),
 * else the oldest member (the origin comes first in a cascade). The returned `listing` is a COPY
 * of that member with `address` / `address_inherited` / `address_source` / `address_conflict`
 * set from displayAddressFor, so every renderer that reads `listing.address` gets the ruling for
 * free and none of them needs a rule of its own.
 */
export function faceForCluster (members, addressState = null) {
  const ms = Array.isArray(members) ? members.filter(Boolean) : []
  if (!ms.length) return { member: null, listing: null, display: displayAddressFor([], addressState) }
  const display = displayAddressFor(ms, addressState)
  const member = display.member || ms.slice().sort(byOldest)[0]
  const listing = {
    ...member,
    ...(display.address ? { address: display.address } : {}),
    address_inherited: display.source === 'canonical' || display.source === 'match' ? false : member.address_inherited,
    address_source: display.source,
    address_conflict: display.conflict,
  }
  return { member, listing, display }
}
